import { msalInstance } from '../App';
import { loginRequest, tokenRenewalConfig } from '../authConfig';

// Time before token expiry to trigger renewal (5 minutes)
const TOKEN_RENEWAL_OFFSET_MS = 5 * 60 * 1000;

class AuthService {
    /**
     * Get active account
     */
    getAccount() {
        const accounts = msalInstance.getAllAccounts();
        return accounts.length > 0 ? accounts[0] : null;
    }

    /**
     * Get token silently and handle auto renewal
     */
    async acquireToken() {
        const account = this.getAccount();
        
        if (!account) {
            throw new Error('No active account! Sign in required.');
        }

        try {
            const response = await msalInstance.acquireTokenSilent({
                ...loginRequest,
                account: account,
            });
            
            // Set timer for token renewal
            this.scheduleTokenRenewal(response.expiresOn);
            
            return response.accessToken;
        } catch (error) {
            console.error('Silent token acquisition failed', error);
            
            // If silent renewal fails, redirect to login
            if (error.name === 'InteractionRequiredAuthError') {
                msalInstance.acquireTokenRedirect(loginRequest);
            }
            throw error;
        }
    }

    /**
     * Schedule token renewal before it expires
     */
    scheduleTokenRenewal(expiresOn) {
        if (!expiresOn) return;
        
        const expiresOnDate = new Date(expiresOn);
        const renewalTime = expiresOnDate.getTime() - TOKEN_RENEWAL_OFFSET_MS;
        const timeUntilRenewal = renewalTime - Date.now();
        
        if (timeUntilRenewal <= 0) {
            // Token is already expired or about to expire
            this.acquireToken().catch(console.error);
            return;
        }
        
        // Clear any existing renewal timers
        if (this.tokenRenewalTimeout) {
            clearTimeout(this.tokenRenewalTimeout);
        }
        
        // Set timer to renew token before it expires
        this.tokenRenewalTimeout = setTimeout(() => {
            this.acquireToken().catch(console.error);
        }, timeUntilRenewal);
        
        console.log(`Token renewal scheduled in ${Math.round(timeUntilRenewal / 1000)} seconds`);
    }

    /**
     * Logout user
     */
    logout() {
        // Clear any token renewal timers
        if (this.tokenRenewalTimeout) {
            clearTimeout(this.tokenRenewalTimeout);
        }
        
        return msalInstance.logoutRedirect();
    }

    /**
     * Check if session is active by verifying token
     */
    async isSessionActive() {
        try {
            await this.acquireToken();
            return true;
        } catch (error) {
            console.error('Session validation failed', error);
            return false;
        }
    }

    /**
     * Set up inactivity monitoring
     * @param {number} inactivityTimeoutMs - Timeout in milliseconds
     */
    setupInactivityMonitor(inactivityTimeoutMs = 30 * 60 * 1000) { // Default 30 min
        let inactivityTimer;
        
        const resetInactivityTimer = () => {
            if (inactivityTimer) clearTimeout(inactivityTimer);
            
            inactivityTimer = setTimeout(() => {
                console.log('User inactive, logging out...');
                this.logout();
            }, inactivityTimeoutMs);
        };
        
        // Reset timer on user activity
        ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'].forEach(event => {
            document.addEventListener(event, resetInactivityTimer, false);
        });
        
        // Initial setup
        resetInactivityTimer();
    }
}

export const authService = new AuthService();
export default authService;